import { useEffect, useMemo, useRef, useState } from 'react';
import X from '@/assets/icons/X.svg';
import * as Shell from '@/app/SearchApp.styled';
import { Checkbox } from '@/components/Checkbox';
import * as Filter from '@/components/FilterPanel/FilterPanel.styled';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ProductTile } from '@/components/ProductTile';
import * as Results from '@/components/ResultsPanel/ResultsPanel.styled';
import * as Refinements from '@/components/SearchHeader/SearchHeader.styled';
import * as Toolbar from '@/components/SearchToolbar/SearchToolbar.styled';
import type { useCategoryListing } from '@/hooks/useCategoryListing';
import { useTrackItemList } from '@/hooks/useTrackItemList';

const ANNOUNCE_DEBOUNCE_MS = 400;
import type { CategoryFacetGroup } from '@/types';
import { toGa4Item } from '@/utils/toGa4Item';
import * as Styled from './CategoryListing.styled';
import { FacetGroup } from './FacetGroup';

type CategoryListingProps = ReturnType<typeof useCategoryListing>;

interface AppliedFilter {
  groupName: string;
  key: string;
  label: string;
}

const buildAppliedFilters = (
  facetGroups: CategoryFacetGroup[],
  selectedFacets: Record<string, string[]>,
  inStock: boolean,
): AppliedFilter[] => {
  const filters: AppliedFilter[] = inStock
    ? [{ groupName: 'inStock', key: 'inStock', label: 'Show in stock only' }]
    : [];

  for (const group of facetGroups) {
    const keys = selectedFacets[group.groupName] ?? [];
    for (const key of keys) {
      filters.push({
        groupName: group.groupName,
        key,
        label: group.facets.find(f => f.key === key)?.name ?? key,
      });
    }
  }

  return filters;
};

export const CategoryListing = (props: CategoryListingProps) => {
  const {
    products,
    totalCount,
    query,
    listName,
    contentId,
    latestPageProducts,
    latestPageOffset,
    resultVersion,
    sorting,
    setSort,
    filter,
    setInStock,
    facetGroups,
    toggleFacet,
    hasActiveFilters,
    clearAll,
    hasMore,
    loadMore,
    isLoadingMore,
    nextPageUrl,
    noResultsLabel,
    noResultsImage,
    showMoreLikeThis,
  } = props;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const appliedFilters = useMemo(
    () =>
      buildAppliedFilters(facetGroups, filter.selectedFacets, filter.inStock),
    [facetGroups, filter.selectedFacets, filter.inStock],
  );

  const railRef = useRef<HTMLDivElement>(null);
  const list = useMemo(
    () => ({
      // `listName` is server-rendered, so a CDN-cached page produced by the old
      // server can load the new bundle mid-deploy and arrive without it.
      name: listName || query || 'category',
      id: contentId ? String(contentId) : undefined,
    }),
    [listName, query, contentId],
  );

  // Newest page only - see the note at the head of this task.
  const ga4Items = useMemo(
    () =>
      latestPageProducts.map((product, i) =>
        toGa4Item(product, list, latestPageOffset + i),
      ),
    [latestPageProducts, latestPageOffset, list],
  );
  // Signature of the data being reported, not of the intent to change it:
  // `resultVersion` bumps in the same update as the products it describes, so
  // a filter change fires exactly once, when the new page lands.
  const { onSelectItem } = useTrackItemList(
    railRef,
    list,
    ga4Items,
    String(resultVersion),
  );

  // #2934 A11Y-08 - WCAG 4.1.3 Status Messages. Applying a facet replaces the grid and
  // changes the count with nothing announced. Debounced so a burst of facet changes
  // coalesces into one announcement instead of queueing several.
  const [announcedCount, setAnnouncedCount] = useState(totalCount);

  useEffect(() => {
    const id = setTimeout(() => setAnnouncedCount(totalCount), ANNOUNCE_DEBOUNCE_MS);

    return () => clearTimeout(id);
  }, [totalCount]);

  if (totalCount === 0 || products.length === 0) {
    return (
      <Styled.NoResults aria-label={`Products in ${query}`}>
        {noResultsImage ? (
          <Styled.NoResultsImage src={noResultsImage} alt="" />
        ) : null}
        <p>{noResultsLabel || 'Sorry, no results found'}</p>
      </Styled.NoResults>
    );
  }

  const tiles = products.map((product, index) => (
    <ProductTile
      key={product.sku ?? index}
      index={index}
      listName={query}
      product={product}
      showMoreLikeThis={showMoreLikeThis}
      onSelect={() => onSelectItem(toGa4Item(product, list, index), index)}
    />
  ));

  return (
    <Shell.Container>
      <Refinements.RefinementsRow>
        {appliedFilters.length > 0 ? (
          <>
            <span>Applied:</span>
            {appliedFilters.map(af => (
              <Refinements.FilterTag
                key={`${af.groupName}-${af.key}`}
                onClick={() =>
                  af.groupName === 'inStock'
                    ? setInStock(false)
                    : toggleFacet(af.groupName, af.key)
                }
                type="button"
              >
                {af.label}
                <X />
              </Refinements.FilterTag>
            ))}
            <Refinements.ClearAllButton onClick={clearAll} type="button">
              Clear all filters
            </Refinements.ClearAllButton>
          </>
        ) : null}
      </Refinements.RefinementsRow>

      <Shell.Layout>
        <Filter.Overlay
          $isOpen={isFilterOpen}
          onClick={() => setIsFilterOpen(false)}
        />
        <Filter.Panel $isOpen={isFilterOpen} aria-label={`Filter ${query}`}>
          <Filter.CloseButton onClick={() => setIsFilterOpen(false)} type="button">
            ✕
          </Filter.CloseButton>

          <Filter.InStockSection>
            <Checkbox
              isSelected={filter.inStock}
              label="Show in stock only"
              onChange={setInStock}
            />
          </Filter.InStockSection>

          {facetGroups.map(group => (
            <FacetGroup
              key={group.groupFieldName}
              group={group}
              onToggle={toggleFacet}
              selectedKeys={filter.selectedFacets[group.groupName] ?? []}
            />
          ))}

          <Filter.MobileButtons>
            {hasActiveFilters ? (
              <Filter.ApplyButton
                onClick={() => {
                  clearAll();
                  setIsFilterOpen(false);
                }}
                type="button"
              >
                Clear all
              </Filter.ApplyButton>
            ) : null}
            <Filter.ApplyButton
              onClick={() => setIsFilterOpen(false)}
              type="button"
            >
              Apply Filters
            </Filter.ApplyButton>
          </Filter.MobileButtons>
        </Filter.Panel>

        <Results.Wrapper ref={railRef}>
          <Toolbar.Wrapper>
            <Toolbar.Left>
              {/* The count is the status message, not the grid: announcing the whole
                  result set would be unusable. role="status" is polite by default; the
                  attributes are explicit so the intent survives a refactor. */}
              <span aria-atomic="true" aria-live="polite" role="status">
                <span className="results-count">
                  {announcedCount.toLocaleString()} results
                </span>
              </span>
            </Toolbar.Left>
            <Toolbar.Right>
              <Toolbar.FilterButton
                onClick={() => setIsFilterOpen(true)}
                type="button"
              >
                Filter
              </Toolbar.FilterButton>
              {sorting.length > 0 ? (
                <Toolbar.SortWrapper>
                  <select
                    aria-label="Sort by"
                    onChange={e => setSort(e.target.value)}
                    value={filter.sort}
                  >
                    {sorting.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                </Toolbar.SortWrapper>
              ) : null}
            </Toolbar.Right>
          </Toolbar.Wrapper>

          <Results.HitsGrid>{tiles}</Results.HitsGrid>

          {hasMore && nextPageUrl ? (
            <Results.ShowMoreWrapper>
              <Styled.ShowMoreLink
                href={nextPageUrl}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  loadMore();
                }}
                aria-busy={isLoadingMore || undefined}
              >
                {isLoadingMore ? <LoadingSpinner /> : 'Show More'}
              </Styled.ShowMoreLink>
            </Results.ShowMoreWrapper>
          ) : null}
        </Results.Wrapper>
      </Shell.Layout>
    </Shell.Container>
  );
};
